import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEmployeeStore } from '../../../store/employeeStore';
import { useRbacStore } from '../../../store/rbacStore';
import { Card, CardContent } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Network, Search, Table2, GitBranch, AlertTriangle, UserPlus, ListTree } from 'lucide-react';
import { Input } from '../../../components/Input';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/Avatar';
import toast from 'react-hot-toast';

interface Emp {
  id: string;
  firstName?: string;
  lastName?: string;
  designation?: string;
  employeeCode?: string;
  reportingManagerId?: string;
  avatar?: string;
}

const fullName = (e?: Emp) => (e ? `${e.firstName || ''} ${e.lastName || ''}`.trim() || 'Unnamed' : '—');

/** Ids of an employee's whole subtree (for cycle-safe manager options). */
function descendantIds(rootId: string, childrenOf: Map<string, Emp[]>): Set<string> {
  const out = new Set<string>();
  const stack = [...(childrenOf.get(rootId) || [])];
  while (stack.length) {
    const n = stack.pop()!;
    if (out.has(n.id)) continue;
    out.add(n.id);
    stack.push(...(childrenOf.get(n.id) || []));
  }
  return out;
}

function ManagerSelect({
  employee, employees, childrenOf, onChange, disabled,
}: {
  employee: Emp; employees: Emp[]; childrenOf: Map<string, Emp[]>;
  onChange: (managerId: string | null) => void; disabled?: boolean;
}) {
  // A manager can be anyone who is not the employee and not in their subtree.
  const blocked = useMemo(() => {
    const s = descendantIds(employee.id, childrenOf);
    s.add(employee.id);
    return s;
  }, [employee.id, childrenOf]);

  const options = employees
    .filter((e) => !blocked.has(e.id))
    .sort((a, b) => fullName(a).localeCompare(fullName(b)));

  return (
    <select
      disabled={disabled}
      value={employee.reportingManagerId || ''}
      onChange={(e) => onChange(e.target.value || null)}
      onClick={(e) => e.stopPropagation()}
      className="max-w-[180px] truncate rounded-md border border-border bg-card px-2 py-1 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50"
      title="Reporting manager"
    >
      <option value="">— No manager (top level) —</option>
      {options.map((m) => (
        <option key={m.id} value={m.id}>
          {fullName(m)}{m.designation ? ` · ${m.designation}` : ''}
        </option>
      ))}
    </select>
  );
}

function OrgNode({
  employee, childrenOf, employees, level, canEdit, onAssign,
}: {
  employee: Emp; childrenOf: Map<string, Emp[]>; employees: Emp[];
  level: number; canEdit: boolean; onAssign: (empId: string, mgrId: string | null) => void;
}) {
  const reports = childrenOf.get(employee.id) || [];
  const [expanded, setExpanded] = useState(level < 2);

  return (
    <div className="flex flex-col items-center">
      <Card className={`w-64 z-10 ${level === 0 ? 'border-primary' : ''}`}>
        <CardContent className="p-3">
          <div className="flex items-center gap-3">
            <Avatar size="sm">
              {employee.avatar && <AvatarImage src={employee.avatar} alt={fullName(employee)} />}
              <AvatarFallback>{employee.firstName?.[0] || '?'}{employee.lastName?.[0] || ''}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="truncate font-medium text-foreground text-sm">{fullName(employee)}</p>
              <p className="truncate text-xs text-muted-foreground">{employee.designation || 'Employee'}</p>
            </div>
          </div>
          {canEdit && (
            <div className="mt-2 flex items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Reports to</span>
              <ManagerSelect
                employee={employee} employees={employees} childrenOf={childrenOf}
                onChange={(mgrId) => onAssign(employee.id, mgrId)}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {reports.length > 0 && (
        <>
          <button
            onClick={() => setExpanded(!expanded)}
            className="my-2 text-xs bg-secondary hover:bg-border text-foreground px-2 py-1 rounded-md z-10 transition-colors"
          >
            {expanded ? '−' : '+'} {reports.length}
          </button>
          {expanded && (
            <div className="relative pt-4 mt-[-16px]">
              <div className="absolute top-0 left-1/2 w-px h-4 bg-border -translate-x-1/2" />
              <div className="flex gap-4 pt-4 relative">
                {reports.map((r) => (
                  <div key={r.id} className="relative">
                    <div className="absolute -top-4 left-1/2 w-px h-4 bg-border -translate-x-1/2" />
                    <OrgNode
                      employee={r} childrenOf={childrenOf} employees={employees}
                      level={level + 1} canEdit={canEdit} onAssign={onAssign}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Vertical, indented tree row — children stack below and indent to the right. */
function VerticalOrgNode({
  employee, childrenOf, employees, depth, canEdit, onAssign,
}: {
  employee: Emp; childrenOf: Map<string, Emp[]>; employees: Emp[];
  depth: number; canEdit: boolean; onAssign: (empId: string, mgrId: string | null) => void;
}) {
  const reports = childrenOf.get(employee.id) || [];
  const [expanded, setExpanded] = useState(depth < 3);

  return (
    <div>
      <div
        className="flex items-center gap-2 py-1"
        style={{ paddingLeft: depth * 24 }}
      >
        {reports.length > 0 ? (
          <button
            onClick={() => setExpanded(!expanded)}
            className="size-5 shrink-0 rounded border border-border bg-secondary text-foreground text-xs leading-none hover:bg-border transition-colors"
            title={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? '−' : '+'}
          </button>
        ) : (
          <span className="size-5 shrink-0" />
        )}
        <Card className={`flex-1 ${depth === 0 ? 'border-primary' : ''}`}>
          <CardContent className="p-2.5">
            <div className="flex items-center gap-3">
              <Avatar size="sm">
                {employee.avatar && <AvatarImage src={employee.avatar} alt={fullName(employee)} />}
                <AvatarFallback>{employee.firstName?.[0] || '?'}{employee.lastName?.[0] || ''}</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-foreground text-sm">{fullName(employee)}</p>
                <p className="truncate text-xs text-muted-foreground">{employee.designation || 'Employee'}</p>
              </div>
              {reports.length > 0 && (
                <span className="shrink-0 text-[10px] text-muted-foreground">{reports.length} report{reports.length > 1 ? 's' : ''}</span>
              )}
              {canEdit && (
                <ManagerSelect
                  employee={employee} employees={employees} childrenOf={childrenOf}
                  onChange={(mgrId) => onAssign(employee.id, mgrId)}
                />
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {expanded && reports.length > 0 && (
        <div>
          {reports.map((r) => (
            <VerticalOrgNode
              key={r.id} employee={r} childrenOf={childrenOf} employees={employees}
              depth={depth + 1} canEdit={canEdit} onAssign={onAssign}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function OrgChart({ title = 'Organization Chart' }: { title?: string }) {
  const { employees, isLoading, error, fetchEmployees, setManager } = useEmployeeStore();
  const canEdit = useRbacStore((s) => s.hasPermission('People', 'Org Chart', 'Edit'));
  const canCreate = useRbacStore((s) => s.hasPermission('People', 'Employees', 'Create'));
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [view, setView] = useState<'chart' | 'vertical' | 'table'>('chart');

  useEffect(() => {
    fetchEmployees({ limit: 1000 });
  }, [fetchEmployees]);

  const byId = useMemo(() => {
    const m = new Map<string, Emp>();
    for (const e of employees as Emp[]) m.set(e.id, e);
    return m;
  }, [employees]);

  // children keyed by manager id
  const childrenOf = useMemo(() => {
    const m = new Map<string, Emp[]>();
    for (const e of employees as Emp[]) {
      const mgr = e.reportingManagerId && byId.has(e.reportingManagerId) ? e.reportingManagerId : null;
      if (mgr && mgr !== e.id) {
        if (!m.has(mgr)) m.set(mgr, []);
        m.get(mgr)!.push(e);
      }
    }
    for (const list of m.values()) list.sort((a, b) => fullName(a).localeCompare(fullName(b)));
    return m;
  }, [employees, byId]);

  // Roots: no manager, or manager missing / pointing at self.
  const roots = useMemo(
    () => (employees as Emp[])
      .filter((e) => !e.reportingManagerId || !byId.has(e.reportingManagerId) || e.reportingManagerId === e.id)
      .sort((a, b) => fullName(a).localeCompare(fullName(b))),
    [employees, byId]
  );

  const unassignedCount = roots.length;

  const handleAssign = async (empId: string, mgrId: string | null) => {
    try {
      await setManager(empId, mgrId);
      const who = fullName(byId.get(empId));
      toast.success(mgrId ? `${who} now reports to ${fullName(byId.get(mgrId))}` : `${who} set to top level`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Could not assign manager');
    }
  };

  const filteredEmployees = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees as Emp[];
    return (employees as Emp[]).filter(
      (e) => fullName(e).toLowerCase().includes(q) ||
        (e.designation || '').toLowerCase().includes(q) ||
        (e.employeeCode || '').toLowerCase().includes(q)
    );
  }, [employees, search]);

  return (
    <div className="space-y-4 flex flex-col h-[calc(100vh-8rem)]">
      <div className="flex flex-wrap justify-between items-center gap-3 shrink-0">
        <div>
          <h1 className="tracking-tight text-foreground font-display text-page-title">{title}</h1>
          <p className="text-muted-foreground">
            Visual reporting structure.{canEdit ? ' Set each person’s manager to build the chart.' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-56">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search employees…" className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="flex rounded-lg border border-border overflow-hidden">
            <button
              onClick={() => setView('chart')}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold ${view === 'chart' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'}`}
            ><GitBranch className="size-4" /> Chart</button>
            <button
              onClick={() => setView('vertical')}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border-l border-border ${view === 'vertical' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'}`}
            ><ListTree className="size-4" /> Vertical</button>
            <button
              onClick={() => setView('table')}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border-l border-border ${view === 'table' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'}`}
            ><Table2 className="size-4" /> Reporting lines</button>
          </div>
          {canCreate && (
            <Button variant="primary" size="sm" onClick={() => navigate('/people/employees/new')}>
              <UserPlus className="size-4" /> Add employee
            </Button>
          )}
        </div>
      </div>

      {canEdit && unassignedCount > 1 && (
        <div className="flex items-center gap-2 rounded-lg border border-warning/30 bg-warning/10 px-4 py-2 text-xs text-warning-strong shrink-0">
          <AlertTriangle className="size-4 shrink-0" />
          {unassignedCount} people have no manager yet. Assign each one below so they sit under the right person in the chart.
        </div>
      )}

      <Card className="flex-1 overflow-auto bg-secondary/20">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-12 text-center text-muted-foreground">Loading org chart…</div>
          ) : error ? (
            <div className="p-12 text-center flex flex-col items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-warning" />
              <p className="text-sm font-medium text-foreground">Couldn’t load the org chart</p>
              <p className="text-xs text-muted-foreground max-w-md">{error}</p>
              <Button variant="secondary" size="sm" onClick={() => fetchEmployees({ limit: 1000 })}>
                Retry
              </Button>
            </div>
          ) : employees.length === 0 ? (
            <div className="p-12 text-muted-foreground flex flex-col items-center">
              <Network className="w-8 h-8 mb-2 opacity-50" />
              No employees found. Add employees to build an org chart.
            </div>
          ) : view === 'chart' ? (
            <div className="p-12 min-w-max flex flex-col items-center gap-10">
              {roots.map((r) => (
                <OrgNode
                  key={r.id} employee={r} childrenOf={childrenOf} employees={employees as Emp[]}
                  level={0} canEdit={canEdit} onAssign={handleAssign}
                />
              ))}
            </div>
          ) : view === 'vertical' ? (
            <div className="p-4 min-w-max">
              {roots.map((r) => (
                <VerticalOrgNode
                  key={r.id} employee={r} childrenOf={childrenOf} employees={employees as Emp[]}
                  depth={0} canEdit={canEdit} onAssign={handleAssign}
                />
              ))}
            </div>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="sticky top-0 bg-card z-10">
                <tr className="border-b border-border">
                  <th className="px-4 py-3 font-semibold text-muted-foreground">Employee</th>
                  <th className="px-4 py-3 font-semibold text-muted-foreground">Designation</th>
                  <th className="px-4 py-3 font-semibold text-muted-foreground">Code</th>
                  <th className="px-4 py-3 font-semibold text-muted-foreground">Reporting manager</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-12">
                {filteredEmployees.map((e) => (
                  <tr key={e.id} className="hover:bg-card/40">
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <Avatar size="sm"><AvatarFallback>{e.firstName?.[0] || '?'}{e.lastName?.[0] || ''}</AvatarFallback></Avatar>
                        <span className="font-medium text-foreground">{fullName(e)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">{e.designation || '—'}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{e.employeeCode || '—'}</td>
                    <td className="px-4 py-2.5">
                      {canEdit ? (
                        <ManagerSelect employee={e} employees={employees as Emp[]} childrenOf={childrenOf} onChange={(m) => handleAssign(e.id, m)} />
                      ) : (
                        <span className="text-muted-foreground">{fullName(byId.get(e.reportingManagerId || '')) || '—'}</span>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredEmployees.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-10 text-center text-muted-foreground">No employees match “{search}”.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
